/**
 * Places data access: places and geofences, visits, assets, beacons and asset rules. Other
 * modules may use these hooks (the live map does), never the pages.
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, unwrap } from '@/lib/api/client';
import type { components } from '@/lib/api/schema';
import { fieldLookup } from '@/lib/fields';

type S = components['schemas'];
export type PlaceOut = S['PlaceOut'];
export type PlaceCreate = S['PlaceCreate'];
export type PlacePatch = S['PlacePatch'];
export type Shape = S['Shape'];
export type VisitOut = S['VisitOut'];
export type VisitSummaryOut = S['VisitSummaryOut'];
export type AssetOut = S['AssetOut'];
export type AssetCreate = S['AssetCreate'];
export type AssetPatch = S['AssetPatch'];
export type AssetsLiveOut = S['AssetsLiveOut'];
export type AssetLiveUnit = S['AssetLiveUnit'];
export type UtilisationItem = S['UtilisationItem'];
export type AssetTripOut = S['AssetTripOut'];
export type AssetTrackOut = S['AssetTrackOut'];
export type BeaconOut = S['BeaconOut'];
export type BeaconCreate = S['BeaconCreate'];
export type BeaconPatch = S['BeaconPatch'];
export type SightingOut = S['SightingOut'];
export type AssetRuleOut = S['AssetRuleOut'];
export type AssetRuleCreate = S['AssetRuleCreate'];
export type AssetRulePatch = S['AssetRulePatch'];
export type AssetRuleDetailOut = S['AssetRuleDetailOut'];
export type AssetRuleForAsset = S['AssetRuleForAsset'];
export type RuleTemplateOut = S['RuleTemplateOut'];
export type RuleStateOut = S['RuleStateOut'];
export type ActionOut = S['ActionOut'];

export const placesKeys = {
  all: ['places'] as const,
  places: (p: object) => ['places', 'places', p] as const,
  place: (id: string) => ['places', 'place', id] as const,
  present: (id: string) => ['places', 'present', id] as const,
  visits: (p: object) => ['places', 'visits', p] as const,
  summary: (p: object) => ['places', 'summary', p] as const,
  assets: (p: object) => ['places', 'assets', p] as const,
  asset: (id: string) => ['places', 'asset', id] as const,
  assetsLiveAll: ['places', 'assets-live'] as const,
  assetsLive: (p: object) => ['places', 'assets-live', p] as const,
  utilisation: (p: object) => ['places', 'utilisation', p] as const,
  assetTrips: (p: object) => ['places', 'asset-trips', p] as const,
  assetTrack: (p: object) => ['places', 'asset-track', p] as const,
  assetRules: (id: string) => ['places', 'asset-rules-of', id] as const,
  beacons: (p: object) => ['places', 'beacons', p] as const,
  beacon: (id: string) => ['places', 'beacon', id] as const,
  sightings: (p: object) => ['places', 'sightings', p] as const,
  rules: (p: object) => ['places', 'rules', p] as const,
  rule: (id: string) => ['places', 'rule', id] as const,
  templates: ['places', 'templates'] as const,
};

const next = (last: { next_cursor?: string | null }) => last.next_cursor ?? undefined;

// ---------------------------------------------------------------- places & visits

export interface PlaceQuery {
  project?: string[];
  kind?: string[];
  status?: 'active' | 'archived' | 'all';
  q?: string;
  tag?: string;
  geofence?: boolean;
}

export function usePlaces(p: PlaceQuery, enabled = true, limit = 200) {
  return useInfiniteQuery({
    queryKey: placesKeys.places({ ...p, limit }),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/places/places', { params: { query: { ...p, cursor: pageParam, limit } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled,
  });
}

export function usePlace(id: string, enabled = true) {
  return useQuery({
    queryKey: placesKeys.place(id),
    queryFn: () => unwrap(api.GET('/api/places/places/{place_id}', { params: { path: { place_id: id } } })),
    enabled: !!id && enabled,
  });
}

export function usePresent(id: string, enabled = true) {
  return useQuery({
    queryKey: placesKeys.present(id),
    queryFn: () => unwrap(api.GET('/api/places/places/{place_id}/present', { params: { path: { place_id: id } } })),
    refetchInterval: 60_000,
    enabled,
  });
}

export interface VisitQuery {
  project?: string[];
  place?: string[];
  vehicle?: string;
  asset?: string;
  unit_type?: 'vehicle' | 'asset';
  status?: 'ongoing' | 'completed';
  kind?: string[];
  start?: string;
  end?: string;
}

export function useVisits(p: VisitQuery, enabled = true, limit = 100) {
  return useInfiniteQuery({
    queryKey: placesKeys.visits({ ...p, limit }),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/places/visits', { params: { query: { ...p, cursor: pageParam, limit } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useVisitSummary(p: Omit<VisitQuery, 'unit_type' | 'status' | 'kind'> & { group_by: 'place' | 'kind' | 'vehicle' | 'asset' }, enabled = true) {
  return useQuery({
    queryKey: placesKeys.summary(p),
    queryFn: () => unwrap(api.GET('/api/places/visits/summary', { params: { query: { ...p, limit: 50 } } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

// ---------------------------------------------------------------- assets

export interface AssetQuery {
  project?: string[];
  status?: 'active' | 'inactive' | 'all';
  q?: string;
  asset_type?: string;
  division?: string;
  tag?: string;
}

export function useAssets(p: AssetQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: placesKeys.assets(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/places/assets', { params: { query: { ...p, cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useAsset(id: string, enabled = true) {
  return useQuery({
    queryKey: placesKeys.asset(id),
    queryFn: () => unwrap(api.GET('/api/places/assets/{asset_id}', { params: { path: { asset_id: id } } })),
    refetchInterval: 60_000,
    enabled,
  });
}

export function useAssetsLive(p: { project?: string[]; asset_type?: string; division?: string; tag?: string }, enabled = true) {
  return useQuery({
    queryKey: placesKeys.assetsLive(p),
    queryFn: () => unwrap(api.GET('/api/places/assets/live', { params: { query: p } })),
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useUtilisation(p: { asset?: string[]; project?: string[]; start?: string; end?: string }, enabled = true) {
  return useQuery({
    queryKey: placesKeys.utilisation(p),
    queryFn: () => unwrap(api.GET('/api/places/assets/utilisation', { params: { query: p } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useAssetTrips(id: string, start?: string, end?: string) {
  return useQuery({
    queryKey: placesKeys.assetTrips({ id, start, end }),
    queryFn: () => unwrap(api.GET('/api/places/assets/{asset_id}/trips', { params: { path: { asset_id: id }, query: { start, end } } })),
    placeholderData: keepPreviousData,
  });
}

export function useAssetTrack(id: string, start: string, end: string | undefined, enabled = true) {
  return useQuery({
    queryKey: placesKeys.assetTrack({ id, start, end }),
    queryFn: () => unwrap(api.GET('/api/places/assets/{asset_id}/track', { params: { path: { asset_id: id }, query: { start, end, max_points: 3000 } } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useRulesOfAsset(id: string, enabled = true) {
  return useQuery({
    queryKey: placesKeys.assetRules(id),
    queryFn: () => unwrap(api.GET('/api/places/assets/{asset_id}/rules', { params: { path: { asset_id: id } } })),
    enabled,
  });
}

// ---------------------------------------------------------------- beacons

export interface BeaconQuery {
  project?: string[];
  kind?: 'asset_tag' | 'driver_keyfob' | 'sensor';
  linked?: boolean;
  status?: 'active' | 'inactive' | 'all';
  q?: string;
}

export function useBeacons(p: BeaconQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: placesKeys.beacons(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/places/beacons', { params: { query: { ...p, cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useBeacon(id: string) {
  return useQuery({
    queryKey: placesKeys.beacon(id),
    queryFn: () => unwrap(api.GET('/api/places/beacons/{beacon_id}', { params: { path: { beacon_id: id } } })),
  });
}

export function useSightings(id: string, start?: string, end?: string) {
  return useQuery({
    queryKey: placesKeys.sightings({ id, start, end }),
    queryFn: () => unwrap(api.GET('/api/places/beacons/{beacon_id}/sightings', { params: { path: { beacon_id: id }, query: { start, end, limit: 200 } } })),
  });
}

// ---------------------------------------------------------------- asset rules

export interface RuleQuery {
  project?: string[];
  kind?: 'threshold' | 'geofence' | 'time_window' | 'connectivity';
  enabled?: boolean;
  status?: 'active' | 'archived' | 'all';
  place?: string;
}

export function useRules(p: RuleQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: placesKeys.rules(p),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/places/asset-rules', { params: { query: { ...p, cursor: pageParam, limit: 200 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: next,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useRule(id: string) {
  return useQuery({
    queryKey: placesKeys.rule(id),
    queryFn: () => unwrap(api.GET('/api/places/asset-rules/{rule_id}', { params: { path: { rule_id: id } } })),
  });
}

export function useTemplates(enabled = true) {
  return useQuery({
    queryKey: placesKeys.templates,
    queryFn: () => unwrap(api.GET('/api/places/asset-rules/templates')),
    staleTime: 30 * 60_000,
    enabled,
  });
}

/**
 * The canonical values a threshold rule can test, with label and unit (from the rule catalogue),
 * as a lookup for `ruleSummary` and the editor. Undefined keys fall back to the key itself.
 */
export function useRuleFields(enabled = true) {
  const q = useTemplates(enabled);
  const fields = q.data?.fields;
  return useMemo(() => ({ fields: fields ?? [], lookup: fieldLookup(fields) }), [fields]);
}

// ---------------------------------------------------------------- writes (actions)

function useWrite<V>(fn: (v: V) => Promise<ActionOut>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: placesKeys.all }),
  });
}

export const useCreatePlace = () => useWrite((body: PlaceCreate) => unwrap(api.POST('/api/places/places', { body })));
export const useUpdatePlace = (id: string) =>
  useWrite((body: PlacePatch) => unwrap(api.PATCH('/api/places/places/{place_id}', { params: { path: { place_id: id } }, body })));
export const useArchivePlace = (id: string) =>
  useWrite((reason: string) => unwrap(api.POST('/api/places/places/{place_id}/archive', { params: { path: { place_id: id } }, body: { reason } })));
export const useRescanVisits = () =>
  useWrite((body: { start: string; end?: string | null; project_id?: string | null; vehicle_ids?: string[]; asset_ids?: string[] }) =>
    unwrap(api.POST('/api/places/visits/rescan', { body })),
  );

export const useCreateAsset = () => useWrite((body: AssetCreate) => unwrap(api.POST('/api/places/assets', { body })));
export const useUpdateAsset = (id: string) =>
  useWrite((body: AssetPatch) => unwrap(api.PATCH('/api/places/assets/{asset_id}', { params: { path: { asset_id: id } }, body })));
export const useLinkAssetDevice = (id: string) =>
  useWrite((deviceId: string | null) =>
    unwrap(api.POST('/api/places/assets/{asset_id}/link-device', { params: { path: { asset_id: id } }, body: { device_id: deviceId } })),
  );

export const useRegisterBeacon = () => useWrite((body: BeaconCreate) => unwrap(api.POST('/api/places/beacons', { body })));
export const useUpdateBeacon = (id: string) =>
  useWrite((body: BeaconPatch) => unwrap(api.PATCH('/api/places/beacons/{beacon_id}', { params: { path: { beacon_id: id } }, body })));
export const useLinkBeacon = (id: string) =>
  useWrite((target: { asset_id?: string | null; driver_id?: string | null }) =>
    unwrap(api.POST('/api/places/beacons/{beacon_id}/link', { params: { path: { beacon_id: id } }, body: target })),
  );

export const useCreateRule = () => useWrite((body: AssetRuleCreate) => unwrap(api.POST('/api/places/asset-rules', { body })));
export const useUpdateRule = (id: string) =>
  useWrite((body: AssetRulePatch) => unwrap(api.PATCH('/api/places/asset-rules/{rule_id}', { params: { path: { rule_id: id } }, body })));
